import {Outlet, useParams, useNavigate, NavLink} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {Skeleton, Tag, Tooltip} from 'antd';
import {ArrowLeftOutlined} from '@ant-design/icons';
import {GET_REFERRAL_PARTNER_BY_ID} from 'pages/referralPartners/resolvers';
import styles from './usersPartnersDashboard.module.scss';
import {useReferralPartnerStore} from '../../../zustand/store';

const TABS = [
  {to: '', label: 'Overview', end: true},
  {to: 'referrals', label: 'Referees'},
  {to: 'earnings', label: 'Earnings'},
];

const initialsOf = (name, email) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return (parts[0]?.[0] || email?.[0] || '?').toUpperCase();
};

/** One-line summary of how this partner is paid on Academy sales. */
const academySummary = commission => {
  if (!commission?.isEnabled) return null;
  const rates = commission.rates || [];
  if (!rates.length) return 'Academy: enabled, no rates';
  const preview = rates
    .slice(0, 2)
    .map(
      rate =>
        `${rate.value}${rate.type === 'FIXED' ? ` ${rate.currency}` : '%'}${
          rate.type === 'FIXED' ? '' : ` ${rate.currency}`
        }`,
    )
    .join(' · ');
  const more = rates.length > 2 ? ` +${rates.length - 2}` : '';
  return `Academy: ${preview}${more}`;
};

export const UserReferralPartnerDashboard = () => {
  const {id: partnerId} = useParams();
  const navigate = useNavigate();
  const {partnerName, partnerEmail} = useReferralPartnerStore();

  const {data, loading} = useQuery(GET_REFERRAL_PARTNER_BY_ID, {
    variables: {partnerId},
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
  });

  const detail =
    data?.adminGetReferralPartnerById?.__typename ===
    'AdminReferralPartnerDetail'
      ? data.adminGetReferralPartnerById
      : null;
  const partner = detail?.partner;

  // Fall back to whatever the list page put in the store while this loads,
  // or if the detail resolver is unavailable.
  const name =
    `${partner?.firstName || ''} ${partner?.lastName || ''}`.trim() ||
    partnerName ||
    'Referral Partner';
  const email = partner?.email || partnerEmail;
  const academy = academySummary(partner?.referralPartnerAcademyCommission);

  return (
    <div className={styles.page}>
      <button
        type="button"
        className={styles.backButton}
        onClick={() => navigate('/dashboard/referral-partners')}>
        <ArrowLeftOutlined /> All partners
      </button>

      <header className={styles.identityBar}>
        <span className={styles.avatar}>{initialsOf(name, email)}</span>
        <div className={styles.identityText}>
          <h1 className={styles.name}>{name}</h1>
          {email && <span className={styles.email}>{email}</span>}
        </div>

        <div className={styles.chips}>
          {loading && !partner ? (
            <Skeleton.Button active size="small" style={{width: 220}} />
          ) : (
            <>
              {partner?.referralCode && (
                <Tooltip title="Referral code">
                  <span className={styles.code}>{partner.referralCode}</span>
                </Tooltip>
              )}
              {partner?.referralPartnerType && (
                <Tag
                  className={styles.typeTag}
                  color={
                    partner.referralPartnerType === 'NONE'
                      ? 'default'
                      : 'purple'
                  }>
                  {partner.referralPartnerType.replace(/_/g, ' ').toLowerCase()}
                </Tag>
              )}
              {academy ? (
                <Tag className={styles.typeTag} color="green">
                  {academy}
                </Tag>
              ) : (
                <Tooltip title="Academy sales are still attributed to this partner, they just pay nothing.">
                  <Tag className={styles.typeTag}>Academy commission off</Tag>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </header>

      <nav className={styles.tabs}>
        {TABS.map(tab => (
          <NavLink
            key={tab.label}
            to={`/dashboard/referral-partners/${partnerId}${
              tab.to ? `/${tab.to}` : ''
            }`}
            end={tab.end}
            className={({isActive}) =>
              isActive ? `${styles.tab} ${styles.tabActive}` : styles.tab
            }>
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <div className={styles.tabBody}>
        <Outlet />
      </div>
    </div>
  );
};
